import remarkParse from 'remark-parse';
import slugify from 'slugify';
import { unified } from 'unified';

export interface MarkdownHeading {
  level: number;
  text: string;
}

export interface NavigationLink {
  name: string;
  description: string;
  key: string;
  children: NavigationLink[];
}

/**
 * Generates a URL-safe slug for a heading, prefixed with `heading/`.
 * @param text - The heading text to slugify.
 * @returns A slug in the format `heading/<slugified-text>`.
 */
export function generateHeadingSlug(text: string): string {
  return 'heading/' + slugify(text.toLowerCase(), { trim: false });
}

/**
 * Creates a deduplicator function that ensures unique slugs by appending a
 * numeric suffix (e.g. `-2`, `-3`) when the same slug is encountered more than once.
 * @returns A function that accepts a slug and returns a unique version of it.
 */
export function createSlugDeduplicator(): (slug: string) => string {
  const counts = new Map<string, number>();
  return (slug: string): string => {
    const count = (counts.get(slug) ?? 0) + 1;
    counts.set(slug, count);
    return count === 1 ? slug : `${slug}-${count}`;
  };
}

export function extractHeadings(markdownContent: string): MarkdownHeading[] {
  const processor = unified().use(remarkParse);
  const parsedMarkdown = processor.parse(markdownContent);

  const headings: MarkdownHeading[] = [];
  parsedMarkdown.children.forEach((node) => {
    if (node.type === 'heading' && node.depth >= 1 && node.depth <= 6) {
      const text = node.children
        .filter((child) => child.type === 'text' || child.type === 'inlineCode')
        .map((child) => child.value)
        .join('');
      headings.push({
        level: node.depth,
        text,
      });
    }
  });

  return headings;
}

export function extractLinks(content: string): NavigationLink[] {
  const headings: MarkdownHeading[] = extractHeadings(content);
  const root: NavigationLink[] = [];

  // Stack tracks the current ancestor chain: stack[0] is the most recent h1 node, stack[1] is h2, etc.
  const stack: { level: number; node: NavigationLink }[] = [];

  const deduplicateSlug = createSlugDeduplicator();

  for (const heading of headings) {
    const node: NavigationLink = {
      name: heading.text,
      description: heading.text,
      key: deduplicateSlug(generateHeadingSlug(heading.text)),
      children: [],
    };

    // Pop stack entries that are at the same level or deeper
    while (stack.length > 0 && stack.at(-1)!.level >= heading.level) {
      stack.pop();
    }

    if (stack.length > 0) {
      stack.at(-1)!.node.children.push(node);
    } else {
      root.push(node);
    }

    stack.push({ level: heading.level, node });
  }

  return root;
}
